// W52.0 D-1 — the dashboard's hero, in one place.
//
// The route renders **two** `DashboardHero`s — the not-paired state and the
// paired one — and before this existed each spelled out its own `title` /
// `eyebrow` / `subtitle`. That is exactly how D-1 happened: the first hero had
// no eyebrow and the second had a different subtitle, and nothing in the type
// system or the tests noticed because both were valid.
//
// `trailing` is passed through untouched so the paired screen keeps its `+`.

import { PageHero } from "@/components/primitives/PageHero";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";

export function DashboardHero({
  dateLabel,
  trailing,
}: {
  dateLabel: string;
  trailing?: React.ReactNode;
}) {
  return (
    <PageHero
      title={DASHBOARD_I18N.hero.title}
      eyebrow="我們的空間"
      // W57.0 — the subtitle is **the date and nothing else**.
      //
      // It used to read 「在一起第 N 天 · 2026年10月26日」, and the hero card
      // right below it carries the same number as its big numeral — the same
      // fact twice on one screen.
      //
      // That was never a decision. `docs/spec/dashboard/README.md` §3.1 says
      // 「PageHero 日期 meta + display-1『首頁』」 and ADR-0054 (spec §10 #2)
      // settled 「標題『首頁』, 日期放 subtitle」 — **neither** put the day
      // count in the subtitle. `dashboardSubtitle` added it in W52.0 D-1
      // without a spec to match, so this is the `AGENTS.md §4` case "spec says
      // X, code does Y": the spec is right and the code was wrong.
      //
      // The number now lives in exactly one place — the hero card's numeral,
      // which is where spec §3.1 puts it (「左:在一起天數(big)」) and where a
      // number this large belongs.
      subtitle={dateLabel}
      trailing={trailing}
      // W42 F34: the trailing slot is where the dashboard's create action
      // lives, and it was missing until this round. It was missing because
      // nobody compared the four F5 情緒型 routes against each other —
      // `diaries` / `wishes` / `anniversaries` all had one and the home page,
      // which is the same kind of screen, had none. The proposal to move every
      // `+` into the topbar surfaced it (§1.4), and that proposal was rejected
      // for three separate reasons, none of which are about the dashboard
      // lacking an entry point. A dashboard create action is the product's own
      // introduction (PRODUCT.md §Operating Context: register → pair → first
      // diary) and the one thing a new pair is on the app to do.
      //
      // W47.2: `DashboardCreateAction` used to open the diary composer
      // **silently**. The label said 「新增」 and the behaviour was one diary —
      // so the button promised anything and delivered a diary, with no step
      // telling the person what had just happened. That component owns the
      // button *and* the panel, so the diary is still the first row rather
      // than the only one.
    />
  );
}
